import React, { PropsWithChildren } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { tokens } from './tokens';

/** The dialog is labelled by whichever screen's title is showing. */
export const TITLE_ID = 'smart-goal-title';

/** Header (optional close X), scrollable body, and a pinned footer — the layout every screen shares. */
export const ScreenShell: React.FC<
  PropsWithChildren<{ onClose?: () => void; footer?: React.ReactNode; background?: string; overlay?: React.ReactNode }>
> = ({ onClose, footer, background = '#fff', overlay, children }) => (
  <Box
    sx={{
      position: 'relative',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background,
      overflow: 'hidden',
    }}
  >
    {overlay}
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        maxWidth: 680, // the 640px content column plus its 20px gutters
        mx: 'auto',
        px: '20px',
        pt: 3,
        pb: 2,
        boxSizing: 'border-box',
      }}
    >
      {onClose ? (
        <IconButton aria-label="Close" onClick={onClose} sx={{ color: tokens.textPrimary }}>
          <Close fontSize="small" />
        </IconButton>
      ) : (
        <Box sx={{ height: 40 }} />
      )}
    </Box>
    <Box sx={{ position: 'relative', zIndex: 1, flex: 1, overflowY: 'auto', px: '20px', pb: 2 }}>
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>{children}</Box>
    </Box>
    {footer && (
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          px: '20px',
          pt: 2,
          pb: 3,
          borderTop: `1px solid ${tokens.borderSubtle}`,
          background: '#fff',
        }}
      >
        <Box sx={{ maxWidth: 640, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>{footer}</Box>
      </Box>
    )}
  </Box>
);

export const ScreenTitle: React.FC<PropsWithChildren<{ large?: boolean }>> = ({ large, children }) => (
  <Typography
    id={TITLE_ID}
    component="h1"
    sx={{
      fontSize: large ? 'clamp(24px, 6.5vw, 28px)' : '19px',
      fontWeight: large ? 700 : 600,
      color: tokens.title,
      letterSpacing: '-0.01em',
      m: 0,
    }}
  >
    {children}
  </Typography>
);

export const BulletList: React.FC<PropsWithChildren> = ({ children }) => (
  <Box
    component="ul"
    sx={{
      fontSize: '14px',
      color: tokens.textPrimary,
      lineHeight: 1.6,
      m: 0,
      pl: '18px',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}
  >
    {children}
  </Box>
);

export const FooterNote: React.FC<{ maxActivities: number }> = ({ maxActivities }) => (
  <Typography sx={{ textAlign: 'center', fontSize: '13px', color: tokens.textPrimary, lineHeight: 1.6 }}>
    You can add up to {maxActivities} activities to your SMART goal.
  </Typography>
);
